import { SurveyV2, UserDto } from '@act/shared/data-transfer-objects';
import { Box } from '@mui/material';
import StepActions from 'react-components/surveys/StepActions';
import { useForm } from 'react-hook-form';
import { applyMustacheTemplate } from '@act/shared/utilities';
import { isValid } from 'zod';
import PromptMock from './PromptMock';
import SurveyInputMock from './SurveyInputMock';
import { useCallback, useEffect, useState } from 'react';

interface QuestionMockProps {
  question: SurveyV2.CreateQuestionJSONDto;
  member: Partial<UserDto>;
  activeStep: number;
  numberOfSteps: number;
  handleChange: (questionKey: string, answer: string | string[]) => void;
  handleStep: (step: number) => void;
}

const QuestionMock = ({
  question,
  member,
  activeStep,
  numberOfSteps,
  handleChange,
  handleStep,
}: QuestionMockProps) => {
  const [prompt, setPrompt] = useState<SurveyV2.CreatePromptJSONDto | null>(
    null
  );

  const { control, watch } = useForm<{
    value: string | number[];
  }>({
    mode: 'onChange',
    defaultValues: {
      value: question.type === 'date' ? null : '',
    },
  });

  const value = watch('value');

  const togglePrompt = useCallback(
    (prompt: SurveyV2.CreatePromptJSONDto) => {
      if (prompt) {
        setPrompt(prompt);
      } else {
        setPrompt(question.prompt);
      }
    },
    [question.prompt]
  );

  useEffect(() => {
    togglePrompt(question.prompt);
  }, [question.prompt, togglePrompt]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {applyMustacheTemplate(question.text, {
        memberName: `${member?.firstName}`,
      })}
      <Box
        sx={{
          margin: '8px 4px 0 0',
          padding: '8px 12px 8px 0',
          '& .survey-input-wrapper': {
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
          },
          '& .survey-input:hover': {
            cursor: 'pointer',
          },
        }}
      >
        <SurveyInputMock
          question={question}
          control={control}
          name={'value'}
          togglePrompt={togglePrompt}
        />
      </Box>
      <PromptMock prompt={prompt} member={member} />
      <StepActions
        activeStep={activeStep}
        numberOfSteps={numberOfSteps}
        disableNext={!isValid}
        handleStep={() => {
          handleChange(question.key, value as string);
          handleStep(activeStep + 1);
        }}
      />
    </Box>
  );
};

export default QuestionMock;
